import React, { useCallback } from 'react';
import { Dialog } from '@mui/material';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';

import { wait } from '@occc-app/utils/utils';
import { useEmailRetailerNetworkScanDataTobaccoSubmissionsCsvReportMutation } from '@occc/graphql';
import { snackbar } from '@occc-app/design-system';

import { TobaccoWeeklySubmissionsTable } from './tobacco-weekly-submissions-table.component';
import {
  TobaccoWeeklySubmissionsFilterForm,
  TobaccoWeeklySubmissionsFilterFormProps,
} from './tobacco-weekly-submissions-filter-form';

import { TobaccoStoresSubmissionsTableContainer } from '../tobacco-stores-submissions-table/tobacco-stores-submissions-table.container';
import { RetailerNetworkScanDataWeeklySubmission } from '../tobacco-submissions.types';
import { useRetailerConnectUser } from '../../../hooks/use-retailer-connect-user.hook';
import { toOCDate } from '../tobacco-submissions.utils';
import { errorMessage } from '../../../retailer-connect.copies';
import { useRetailerNetworkScanDataTobaccoSubmissions } from '../../../hooks/use-retailer-network-scan-data-tobacco-submissions.hook';

type TobaccoWeeklySubmissionsFilterValues = {
  retailerNetworkUuidEquals: string;
  submittedAtFrom?: Date;
  submittedAtTo?: Date;
};

export type TobaccoWeeklySubmissionsTableContainerProps = {
  retailerNetworkUuid: string;
};

export const TobaccoWeeklySubmissionsTableContainer = ({
  retailerNetworkUuid,
}: TobaccoWeeklySubmissionsTableContainerProps) => {
  const { userEmail } = useRetailerConnectUser();
  const [storesSubmissionsDate, setStoresSubmissionsDate] = React.useState<Date | undefined>();

  const [sendReport, { loading: isDownloading }] =
    useEmailRetailerNetworkScanDataTobaccoSubmissionsCsvReportMutation({
      onCompleted: () => {
        snackbar.success(`A download link has been sent to ${userEmail}.`);
      },
      onError: () => {
        snackbar.error(errorMessage);
      },
    });

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'tobacco-weekly-submissions-filter',
  });

  const [filter, setFilter] = React.useState<TobaccoWeeklySubmissionsFilterValues>({
    retailerNetworkUuidEquals: retailerNetworkUuid,
  });

  const { data, loading, error, paginationProps } = useRetailerNetworkScanDataTobaccoSubmissions({
    filter: {
      retailerNetworkUuidEquals: retailerNetworkUuid,
      submittedAtFromEquals: filter.submittedAtFrom?.toISOString(),
      submittedAtToEquals: filter.submittedAtTo?.toISOString(),
    },
  });

  const isFiltersSelected = Boolean(filter.submittedAtFrom || filter.submittedAtTo);

  const onFilterFormSubmit = useCallback<TobaccoWeeklySubmissionsFilterFormProps['onSubmit']>(
    (values) => {
      setFilter({ retailerNetworkUuidEquals: retailerNetworkUuid, ...values });
      popupState.close();
    },
    [popupState, retailerNetworkUuid],
  );

  const onFilterFormReset = useCallback(() => {
    popupState.close();
    wait(300).then(() => {
      // close popup, then reset filter after animation
      setFilter({ retailerNetworkUuidEquals: retailerNetworkUuid });
    });
  }, [popupState, retailerNetworkUuid]);

  const onDownloadReport = useCallback(() => {
    if (!userEmail) {
      return;
    }
    const { submittedAtFrom, submittedAtTo } = filter;
    void sendReport({
      variables: {
        emailToSendReport: userEmail,
        filter: {
          retailerNetworkUuidEquals: retailerNetworkUuid,
          submittedAtFromEquals: submittedAtFrom ? toOCDate(submittedAtFrom) : undefined,
          submittedAtToEquals: submittedAtTo ? toOCDate(submittedAtTo) : undefined,
        },
      },
    });
  }, [filter, retailerNetworkUuid, sendReport, userEmail]);

  const onViewAllStores = useCallback((submission: RetailerNetworkScanDataWeeklySubmission) => {
    setStoresSubmissionsDate(new Date(submission.submittedAt));
  }, []);

  return (
    <>
      <TobaccoWeeklySubmissionsTable
        data={data}
        loading={loading}
        error={error}
        paginationProps={paginationProps}
        isFiltersSelected={isFiltersSelected}
        onFilterClick={(e) => bindTrigger(popupState).onClick(e)}
        isDownloading={isDownloading}
        onDownloadReportClick={onDownloadReport}
        onViewAllStoresClick={onViewAllStores}
      />

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        sx={{
          '.MuiPopover-paper': {
            maxWidth: (theme) => theme.spacing(52),
            minWidth: (theme) => theme.spacing(40),
          },
        }}
      >
        <TobaccoWeeklySubmissionsFilterForm
          onSubmit={onFilterFormSubmit}
          onReset={onFilterFormReset}
          initialValues={filter}
        />
      </Popover>

      <Dialog
        open={!!storesSubmissionsDate}
        onClose={() => setStoresSubmissionsDate(undefined)}
        maxWidth='md'
        fullWidth
      >
        {storesSubmissionsDate && (
          <TobaccoStoresSubmissionsTableContainer
            submittedAt={storesSubmissionsDate}
            retailerNetworkUuid={retailerNetworkUuid}
          />
        )}
      </Dialog>
    </>
  );
};
